import { getPrograms, KameProgram, KameRecognitionDistribution, KameRewardDistribution } from 'app/api/kame';
import { SessionAppState } from 'app/dux/session';
import { getDelegatedOrCurrentCompanyId } from 'app/dux/session/selectors';
import { groupBy, keyBy } from 'lodash';
import { Action, Dispatch } from 'redux';
import { CompanyMap, withCompany } from 'utils/companyDux';

export interface RecognitionDistribution extends KameRecognitionDistribution {
    program: KameProgram;
    rewardDistribution: KameRewardDistribution;
}

export interface CompanyProgramsState {
    distributionsByRoomId?: { [roomId: string]: RecognitionDistribution[] | undefined };
    recognitionDistributions?: { [id: string]: RecognitionDistribution | undefined };
}

export interface ProgramsState {
    [companyId: string]: CompanyProgramsState;
}

export interface ProgramsAppState {
    programs: CompanyMap<CompanyProgramsState>;
}

type RelevantAppState = ProgramsAppState & SessionAppState;

enum ActionTypes {
    PROGRAMS_LOADED = 'PROGRAMS__PROGRAMS_LOADED',
}

interface ProgramsLoadedAction extends Action<ActionTypes.PROGRAMS_LOADED> {
    companyId: string;
    programs: KameProgram[];
}

type ProgramsAction = ProgramsLoadedAction;

export const programsReducer = withCompany((state: ProgramsState = {}, action: ProgramsAction): ProgramsState => {
    switch (action.type) {

        case ActionTypes.PROGRAMS_LOADED: {
            const list = action.programs.flatMap((program) => (
                program.rewardDistributions.flatMap((rewardDistribution) => (
                    rewardDistribution.recognitionDistribution.map((recognitionDistribution): RecognitionDistribution => {
                        return {
                            ...recognitionDistribution,
                            program,
                            rewardDistribution,
                        };
                    })
                ))
            ));

            return {
                ...state,
                distributionsByRoomId: groupBy(list, item => item.roomId),
                recognitionDistributions: keyBy(list, item => item.recognitionDistributionId),
            };
        }
    }

    return state;
});

export const loadPrograms = () =>
    async (dispatch: Dispatch<ProgramsAction>, getState: () => RelevantAppState) => {
        const state = getState();
        const companyId = getDelegatedOrCurrentCompanyId(state);
        const programs = await getPrograms();
        dispatch({ type: ActionTypes.PROGRAMS_LOADED, companyId, programs });
    };

export const getProgramsState = (state: RelevantAppState) => state.programs[getDelegatedOrCurrentCompanyId(state)] ?? {};

export const getRecognitionDistributions = (state: RelevantAppState): { [groupId: string]: RecognitionDistribution | undefined } =>
    getProgramsState(state).recognitionDistributions ?? {};
export const getRecognitionDistribution = (id: string) => (state: RelevantAppState): RecognitionDistribution | undefined =>
    getProgramsState(state).recognitionDistributions?.[id];

export const getDistributionsByRoomId = (state: RelevantAppState): { [groupId: string]: RecognitionDistribution[] | undefined } =>
    getProgramsState(state).distributionsByRoomId ?? {};
export const getDistributionsForRoom = (roomId: string) => (state: RelevantAppState): RecognitionDistribution[] =>
    getProgramsState(state).distributionsByRoomId?.[roomId] ?? [];
